<template>
  <div class="stretch">
    <div class="title-padding">
      <el-button
        size="small"
        type="primary"
        class="action"
        :disabled="domains.length === 0"
        @click="open()"
      >
        Add redirection
      </el-button>

      <el-divider content-position="left">
        <h3>HTTP Redirections</h3>
      </el-divider>
    </div>

    <el-table
      v-if="domains.length > 0"
      v-loading="loading"
      :data="redirections"
      ref="redirections"
      size="mini"
    >
      <el-table-column width="24px" />

      <el-table-column label="Forwarding URL">
        <template slot-scope="scope">
          {{ scope.row.domainName }}/{{ scope.row.uri }}
        </template>
      </el-table-column>

      <el-table-column label="Status Code" width="140">
        <template slot-scope="scope">
          <span v-if="scope.row.code === 301">301 - Permanent</span>
          <span v-else>302 - Temporary</span>
        </template>
      </el-table-column>

      <el-table-column prop="dest" label="URL" />

      <el-table-column align="right" width="150">
        <template slot-scope="scope">
          <el-button
            type="default"
            icon="el-icon-edit"
            size="mini"
            @click="open(scope.row)"
          >
            Edit
          </el-button>
        </template>
      </el-table-column>
    </el-table>

    <el-card v-else class="align-center" shadow="hover">
      <h2>Create redirects easily</h2>
      <p>When you have domain names, you can create redirects to other pages and redirects depending on the user's country..</p>
      <el-button type="primary" @click="$emit('update:menu', 'domain')">
        Add new domains
      </el-button>
    </el-card>

    <el-dialog :title="title" width="70%" :visible.sync="dialog" append-to-body>
      <el-alert
        type="info"
        title="Using the asterisk (*) character"
        description="You can create dynamic patterns that can match many URLs, rather than just one."
        show-icon
        :closable="false"
      />

      <el-form ref="redirection" :model="form" :rules="rules">
        <el-row :gutter="10">
          <el-col :span="12">
            <el-form-item prop="domainName">
              <el-select
                v-model="form.domainName"
                default-first-option
                filterable
              >
                <el-option
                  v-for="domain in domains"
                  :key="domain.name"
                  :value="domain.name"
                  :label="domain.name + '/'"
                />
              </el-select>
            </el-form-item>
          </el-col>

          <el-col :span="12">
            <el-form-item prop="uri">
              <el-input v-model="form.uri" placeholder="Example: *" />
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="10">
          <el-col :span="14">
            <el-form-item prop="dest">
              <el-input v-model="form.dest" placeholder="Enter destination URL" />
            </el-form-item>
          </el-col>

          <el-col :span="6">
            <el-form-item prop="code">
              <el-select v-model="form.code" placeholder="Select Status Code">
                <el-option label="301 - Permanent Redirect" :value="301" />
                <el-option label="302 - Temporary Redirect" :value="302" />
              </el-select>
            </el-form-item>
          </el-col>

          <el-col :span="4">
            <el-form-item prop="code">
              <el-select v-model="form.args" placeholder="Query Args">
                <el-option label="Pass" value="pass" />
                <el-option label="Drop" value="drop" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>

        <el-divider content-position="left">Redirection based on geolocation</el-divider>

        <el-row :gutter="10">
          <el-col :span="12">
            <el-select
              v-model="form.countries"
              placeholder="Choose one or more countries (optional)"
              filterable
              clearable
              multiple
            >
              <el-option
                v-for="(country, code) in countries"
                :key="code"
                :label="country.name"
                :value="code"
              />
            </el-select>
          </el-col>

          <el-col :span="12">
            <el-select
              v-model="form.continents"
              placeholder="Choose one or more continents (optional)"
              filterable
              clearable
              multiple
            >
              <el-option
                v-for="(continent, code) in continents"
                :key="code"
                :label="continent"
                :value="code"
              />
            </el-select>
          </el-col>
        </el-row>
      </el-form>

      <div slot="footer">
        <el-button v-if="form.id" type="danger" size="mini" @click="remove()">
          Delete
        </el-button>

        <el-button @click="dialog = false">
          Cancel
        </el-button>

        <el-button type="primary" @click="save()">
          Save
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import _ from 'lodash'
import async from 'async'
import { countries, continents } from 'countries-list'

export default {
  props: {
    instance: Object,
    application: Object,
    domains: {
      type: Array,
      default: () => []
    },
    menu: String
  },
  data () {
    const checkUriPath = (rule, value, callback) => {
      if (!value || value.match(/^http(s)?:\/\//i)) {
        return callback(new Error('Please input URL (without http:// or https://)'))
      }
      callback()
    }
    const checkUrl = (rule, value, callback) => {
      if (!value || !value.match(/^http(s)?:\/\//i)) {
        return callback(new Error('Please input destination URL'))
      }
      callback()
    }
    const sortedCountries = []
    for (const code in countries) {
      sortedCountries.push({
        code,
        ...countries[code]
      })
    }
    sortedCountries.sort((a, b) => a.name.localeCompare(b.name))
    return {
      dialog: false,
      loading: false,
      form: {},
      redirections: [],
      countries: sortedCountries,
      continents,
      rules: {
        uri: [{ validator: checkUriPath, trigger: 'blur' }],
        dest: [{ validator: checkUrl, trigger: 'blur' }],
        domainName: [{ message: 'Please select a domain', required: true, trigger: 'blur' }]
      }
    }
  },
  created () {
    this.load()
  },
  computed: {
    title () {
      return this.form.id ? 'Edit redirection' : 'Add a new redirection'
    }
  },
  methods: {
    async load () {
      if (this.loading || this.domains.length === 0) {
        return
      }

      this.loading = true
      this.redirections = []

      async.each(this.domains, (domain, done) => {
        this.$store.dispatch('getDomainRedirections', domain.name).then((redirections) => {
          this.redirections = _.concat(this.redirections, redirections)
          done()
        }).catch(done)
      }, () => {
        this.loading = false
      })
    },
    remove () {
      this.$confirm('Warning', 'Are you sure ?', {
        confirmButtonText: 'Delete',
        cancelButtonText: 'Cancel',
        type: 'info'
      }).then(() => {
        const index = this.redirections.findIndex((r) => r === this.form)
        if (index !== -1) {
          this.redirections.splice(index, 1)
        }
        this.$store.dispatch('removeDomainRedirection', this.form)
        this.dialog = false
      }).catch(() => {})
    },
    save () {
      this.$refs.redirection.validate((valid) => {
        if (valid) {
          if (this.form.id) {
            this.$store.dispatch('updateDomainRedirection', this.form)
          } else {
            this.$store.dispatch('createDomainRedirection', this.form)
              .then((redirection) => this.redirections.push(redirection))
          }
          this.dialog = false
        }
      })
    },
    open (redirection) {
      this.form = redirection || {
        id: null,
        domainName: null,
        uri: '',
        dest: '',
        code: null,
        args: null,
        countries: [],
        continents: []
      }
      this.dialog = true
    }
  }
}
</script>

<style lang="scss" scoped>
  .el-table {
    margin-top: 12px;
  }
  .el-alert {
    margin-top: 0;
    margin-bottom: 20px;
  }
  .el-select {
    width: 100%;
  }
  .el-card {
    margin: 12px 24px;
  }
  .el-button--danger {
    float: left;
  }
</style>
